import { FocusMetrics, PeriodBreakdownItem, StatsResponse } from '../../models/stats.models';
import {
  FOCUS_CONFIGS,
  MetricMode,
  formatDistributionPercent,
  formatDistributionRawValue,
  getDurationDistanceMetric,
  getFocusColor,
  getFocusLabel,
  normalizeKey,
} from '../../pages/training-log/training-log.helpers';

export type { MetricMode };
export { formatDistributionPercent, formatDistributionRawValue };

export interface FocusDonutItem {
  key: string;
  name: string;
  percent: number;
  value: number;
  rawValue: number;
  color: string;
}

export interface PeriodFocusChartRow {
  label: string;
  [focusKey: string]: string | number;
}

export const getFocusKeys = (
  focusBreakdown: Record<string, FocusMetrics> | undefined,
  periods: PeriodBreakdownItem[]
): string[] => {
  const durationByKey = new Map<string, number>();

  Object.entries(focusBreakdown ?? {}).forEach(([key, metrics]) => {
    durationByKey.set(key, (durationByKey.get(key) ?? 0) + (metrics?.duration ?? 0));
  });

  periods.forEach((period) => {
    Object.entries(period.focus ?? {}).forEach(([key, metrics]) => {
      durationByKey.set(key, (durationByKey.get(key) ?? 0) + (metrics?.duration ?? 0));
    });
  });

  return Array.from(durationByKey.entries())
    .filter(([, duration]) => duration > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([key]) => key);
};

export const getFocusKeysForPeriodLegend = (
  focusBreakdown: Record<string, FocusMetrics> | undefined,
  periods: PeriodBreakdownItem[]
): string[] => {
  const keysWithData = getFocusKeys(focusBreakdown, periods);
  const keysSet = new Set(keysWithData);

  const configuredKeys = FOCUS_CONFIGS.map((config) =>
    keysWithData.find((key) => normalizeKey(key) === normalizeKey(config.key))
  ).filter((key): key is string => Boolean(key));

  const extraKeys = keysWithData.filter(
    (key) => !FOCUS_CONFIGS.some((config) => normalizeKey(config.key) === normalizeKey(key))
  );

  const orderedKeys = [...configuredKeys, ...extraKeys];

  keysWithData.forEach((key) => {
    if (!orderedKeys.includes(key) && keysSet.has(key)) {
      orderedKeys.push(key);
    }
  });

  return orderedKeys;
};

export const buildFocusDonutData = (
  focusBreakdown: Record<string, FocusMetrics> | undefined,
  mode: MetricMode
): FocusDonutItem[] => {
  const entries = Object.entries(focusBreakdown ?? {}).map(([key, metrics], index) => ({
    key,
    name: getFocusLabel(key),
    rawValue: getDurationDistanceMetric(metrics, mode),
    color: getFocusColor(key, index),
  }));

  const total = entries.reduce((sum, entry) => sum + entry.rawValue, 0);

  return entries
    .filter((entry) => entry.rawValue > 0)
    .map((entry) => ({
      ...entry,
      percent: total > 0 ? (entry.rawValue / total) * 100 : 0,
      value: total > 0 ? (entry.rawValue / total) * 100 : 0,
    }))
    .sort((a, b) => b.rawValue - a.rawValue);
};

export const buildPeriodFocusPercentData = (
  periods: PeriodBreakdownItem[],
  focusKeys: string[]
): PeriodFocusChartRow[] => {
  return periods.map((period) => {
    const row: PeriodFocusChartRow = { label: `P${period.index}` };

    focusKeys.forEach((focusKey) => {
      row[focusKey] = period.focus?.[focusKey]?.duration ?? 0;
    });

    return row;
  });
};

export const hasFocusData = (stats: StatsResponse | null): boolean => {
  if (!stats) {
    return false;
  }

  const seasonTotal = Object.values(stats.focusBreakdown ?? {}).reduce(
    (sum, metrics) => sum + (metrics?.duration ?? 0),
    0
  );

  if (seasonTotal > 0) {
    return true;
  }

  return (stats.periodBreakdown ?? []).some((period) =>
    Object.values(period.focus ?? {}).some((metrics) => (metrics?.duration ?? 0) > 0)
  );
};
